import { Button, ButtonVariant, Icon, IconSize, IconVariants, Text, TextVariants, useMedia } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import getUserPlan from "@/features/user/utils/getUserPlan";
import { cn } from "@/utils";

import { markFloatingCTADismissed, markFloatingCTAShown, shouldShowFloatingCTA } from "./cappedConversationStorage";
import type { FloatingUpgradeCtaProps, UpgradeCTAContent } from "./types";
import { useEffectiveCappedMessageId } from "./useEffectiveCappedMessageId";

const getPrimaryCtaContent = (): UpgradeCTAContent => ({
  icon: "🧠",
  title: <Trans i18nKey="chat:page.contextCap.floatingCta.primary.title" />,
  description: <Trans i18nKey="chat:page.contextCap.floatingCta.primary.description" />,
  highlight: <Trans i18nKey="chat:page.contextCap.floatingCta.primary.highlight" className="font-semibold text-white" />,
});

const getAdditionalCtaContent = (): UpgradeCTAContent[] => [
  {
    icon: "📝",
    title: <Trans i18nKey="chat:page.contextCap.floatingCta.moreControl.title" />,
    description: <Trans i18nKey="chat:page.contextCap.floatingCta.moreControl.description" />,
    highlight: <Trans i18nKey="chat:page.contextCap.floatingCta.moreControl.highlight" className="font-semibold text-white" />,
  },
  {
    icon: "🖼️",
    title: <Trans i18nKey="chat:page.contextCap.floatingCta.unlockImages.title" />,
    description: <Trans i18nKey="chat:page.contextCap.floatingCta.unlockImages.description" />,
    highlight: <Trans i18nKey="chat:page.contextCap.floatingCta.unlockImages.highlight" className="font-semibold text-white" />,
  },
  {
    icon: "🤖",
    title: <Trans i18nKey="chat:page.contextCap.floatingCta.advancedModels.title" />,
    description: <Trans i18nKey="chat:page.contextCap.floatingCta.advancedModels.description" />,
    highlight: <Trans i18nKey="chat:page.contextCap.floatingCta.advancedModels.highlight" className="font-semibold text-white" />,
  },
  {
    icon: "🔊",
    title: <Trans i18nKey="chat:page.contextCap.floatingCta.hearChatbot.title" />,
    description: <Trans i18nKey="chat:page.contextCap.floatingCta.hearChatbot.description" />,
    highlight: <Trans i18nKey="chat:page.contextCap.floatingCta.hearChatbot.highlight" className="font-semibold text-white" />,
  },
];

// Probability threshold for showing primary CTA vs additional CTAs (50/50 split)
const PRIMARY_CTA_PROBABILITY = 0.5;

const FloatingUpgradeCta = ({ visible, conversationId, cappedMessageId, className }: FloatingUpgradeCtaProps) => {
  const media = useMedia();
  const { t } = useTranslation();
  const isDesktop = media?.isDesktop ?? true;
  const { hasUserSubscribedPlan } = getUserPlan();
  const [stateIsDismissed, setStateIsDismissed] = useState(false);
  const refHasBeenShownThisSession = useRef(false);
  const refLastConversationId = useRef(conversationId);

  if (conversationId !== refLastConversationId.current) {
    refLastConversationId.current = conversationId;
    refHasBeenShownThisSession.current = false;
  }

  const effectiveCappedMessageId = useEffectiveCappedMessageId({
    conversationId: conversationId ?? null,
    cappedMessageId,
  });
  const isEffectivelyCapped = visible || Boolean(effectiveCappedMessageId);

  const shouldShow = useMemo(() => {
    if (!isEffectivelyCapped || stateIsDismissed || hasUserSubscribedPlan) return false;
    if (!conversationId || !effectiveCappedMessageId) return false;
    if (refHasBeenShownThisSession.current) return true;
    return shouldShowFloatingCTA({ conversationId, lastCappedMessageId: effectiveCappedMessageId });
  }, [isEffectivelyCapped, stateIsDismissed, hasUserSubscribedPlan, conversationId, effectiveCappedMessageId]);

  const ctaContent = useMemo(() => {
    const primaryCtaContent = getPrimaryCtaContent();
    const additionalCtaContent = getAdditionalCtaContent();

    if (Math.random() < PRIMARY_CTA_PROBABILITY) {
      return primaryCtaContent;
    }
    const randomIndex = Math.floor(Math.random() * additionalCtaContent.length);
    return additionalCtaContent[randomIndex];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- conversationId intentionally triggers re-randomization when switching conversations
  }, [conversationId]);

  useEffect(() => {
    setStateIsDismissed(false);
  }, [conversationId]);

  useEffect(() => {
    if (shouldShow && conversationId && effectiveCappedMessageId && !refHasBeenShownThisSession.current) {
      refHasBeenShownThisSession.current = true;
      markFloatingCTAShown({ conversationId, lastCappedMessageId: effectiveCappedMessageId });
    }
  }, [shouldShow, conversationId, effectiveCappedMessageId]);

  const handleDismiss = useCallback(() => {
    setStateIsDismissed(true);
    if (conversationId && effectiveCappedMessageId) {
      markFloatingCTADismissed({ conversationId, lastCappedMessageId: effectiveCappedMessageId });
    }
  }, [conversationId, effectiveCappedMessageId]);

  if (!shouldShow) return null;

  return (
    <div
      className={cn(
        "fixed z-50 transition-all duration-300 ease-in-out",
        // Position and width
        isDesktop ? "bottom-24 right-6 max-w-sm" : "bottom-20 left-4 right-4",
        // Animation
        "animate-in fade-in slide-in-from-bottom-4",
        className,
      )}
      data-testid="FloatingUpgradeCta"
    >
      <div className={cn("relative bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-lg", "border border-purple-400/30", "p-4")}>
        <button
          onClick={handleDismiss}
          className={cn(
            "absolute top-2 right-2 w-6 h-6 flex items-center justify-center",
            "text-white hover:text-white transition-colors",
            "rounded-full hover:bg-white/20 bg-black/20",
          )}
          aria-label={t("common:close")}
          data-testid="FloatingUpgradeCta-DismissButton"
        >
          <Icon icon={IconVariants.CLOSE} size={IconSize.XSMALL} />
        </button>

        <div className="flex flex-col items-center text-center gap-3">
          <div className="flex-shrink-0">
            <span className="text-3xl">{ctaContent.icon}</span>
          </div>

          <div className="flex flex-col items-center gap-1">
            <Text variant={TextVariants.LABEL_SMALL_BOLD} className="text-white">
              {ctaContent.title}
            </Text>
            <Text variant={TextVariants.PARAGRAPH_XSMALL_REGULAR} className="text-white/80">
              {ctaContent.description}
            </Text>
            <Text variant={TextVariants.PARAGRAPH_XSMALL_REGULAR} className="text-white/80">
              <Trans i18nKey="chat:page.contextCap.floatingCta.upgradeFor" /> {ctaContent.highlight}
            </Text>
          </div>

          <LocalizedLink to={ROUTES.subscribe} className="w-full">
            <Button variant={ButtonVariant.INVERSE} className="w-full">
              <Trans i18nKey="chat:page.contextCap.floatingCta.upgradeNow" />
            </Button>
          </LocalizedLink>
        </div>

        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur-lg opacity-30 -z-10" />
      </div>
    </div>
  );
};

export default FloatingUpgradeCta;
